import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { LINK_TONE_CLASSES } from '../config/families';
import { VERIFICATION_DISPLAY, type DocVerificationState } from '../config/verification';

/**
 * The verification chip — the THIRD status axis (86d3x48q7 T13, bundle §4.3).
 *
 * Sits AFTER the link-status and version chips and never replaces either. A scan can be decoded but
 * unverified, or confirmed but unlinked; folding this into one of the other chips would make a
 * single control mean two things and destroy the distinction the calibration window measures.
 *
 * Borrows `LINK_TONE_CLASSES` rather than inventing tints, so all three themes (light, dark and the
 * light-based Industrial, where `dark:` variants never fire) are covered by construction.
 *
 * Every state carries its tooltip from {@link VERIFICATION_DISPLAY}, verbatim from the design
 * bundle — and every one of them is written to avoid implying the AI is authoritative. `Confirmed`
 * says "that is agreement, not a guarantee" in as many words.
 */
interface VerificationChipProps {
  state: DocVerificationState;
  className?: string;
}

export function VerificationChip({ state, className }: VerificationChipProps) {
  const config = VERIFICATION_DISPLAY[state];
  const Icon = config.icon;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
            LINK_TONE_CLASSES[config.tone],
            className
          )}
        >
          <Icon
            // motion-safe kills the spin under prefers-reduced-motion while the label stays —
            // the state is still legible, it just stops moving.
            className={cn('h-3 w-3 flex-shrink-0', config.spins && 'motion-safe:animate-spin')}
            aria-hidden
          />
          {config.label}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{config.tooltip}</TooltipContent>
    </Tooltip>
  );
}
